import { useState, useCallback, useEffect } from 'react';
import { fetchLocations } from '../services/manage-locations.service';
import { formatLocationData, formatLastRefreshedTime } from '../utils/location-formatter';

const INITIAL_PAGE_SIZE = 50;

export const useManageLocations = ({ history }) => {
  const [dataLoading, setDataLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  /**
   * Navigate to the location settings page for a given location.
   * @param {Object} data - The location object to navigate with.
   */
  const goToLocationSettings = useCallback(
    (data) => {
      history.push({
        pathname: `/manageLocations/${data.locationName.replace(/[^a-zA-Z0-9-_]/g, '')}`,
        state: [data],
      });
    },
    [history]
  );

  /**
   * Fetches all locations from the API, formats them, and updates state.
   */
  const getLocations = useCallback(async () => {
    setDataLoading(true);
    try {
      const data = await fetchLocations();

      const formattedLocations = data.locations
        .map((location) => formatLocationData(location, data.locations, goToLocationSettings))
        .filter(Boolean)
        .sort((a, b) => a.rowData.locationName.localeCompare(b.rowData.locationName));

      setLocations(formattedLocations);
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    } finally {
      setDataLoading(false);
    }
  }, [goToLocationSettings]);

  /**
   * Adds a new location to the existing location list and re-sorts.
   * @param {Object} location - Location object to add.
   */
  const updateLocations = useCallback(
    (location) => {
      setLocations((prevLocations) => {
        const updated = [...prevLocations];
        const formatted = formatLocationData(location, updated, goToLocationSettings);
        updated.push(formatted);

        return updated.sort((a, b) => a.rowData.locationName.localeCompare(b.rowData.locationName));
      });
    },
    [goToLocationSettings]
  );

  /**
   * Handles pagination page change.
   * @param {number} newPage - New page number.
   */
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  /**
   * Handles change in number of items per page.
   * @param {number} newPageSize - New page size.
   */
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  /**
   * Updates selected locations based on user interaction.
   * @param {Array} selectedRows - Array of selected location IDs or rows.
   */
  const handleSelectionChange = useCallback((selectedRows) => {
    setSelectedLocations(selectedRows);
  }, []);

    /**
   * Runs once on component mount to fetch and load locations.
   * The dependency on `getLocations` ensures the effect
   * re-runs if the function reference changes (usually stable due to useCallback).
   */
  useEffect(() => {
    getLocations();
  }, [getLocations]);


  return {
    pagination: {
      page,
      pageSize,
      handlePageChange,
      handlePageSizeChange,
    },
    selection: {
      selectedLocations,
      setSelectedLocations,
      handleSelectionChange,
    },
    loadingState: {
      dataLoading,
      getLocations,
      lastRefreshed,
      formatLastRefreshedTime,
    },
    locationData: {
      locations,
      updateLocations,
      goToLocationSettings,
    },
  };
};
